import { Link } from 'react-router-dom';

/**
 * Radiance Hub brand mark + lockup.
 * Botanical six-petal bloom in rose-gold gradient with a soft halo,
 * paired with a Cormorant Garamond wordmark.
 *
 * Variants:
 *  - mark: the icon only
 *  - lockup: icon + wordmark side by side (default)
 *  - stacked: icon above wordmark (for hero / login)
 */

export const BrandMark = ({ size = 36, className = '', withHalo = true }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 64 64"
    width={size}
    height={size}
    role="img"
    aria-label="Radiance Hub"
    className={className}
    data-testid="brand-mark"
  >
    <defs>
      <radialGradient id="rh-mark-halo" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFF1EC" />
        <stop offset="100%" stopColor="#F4D8CD" />
      </radialGradient>
      <linearGradient id="rh-mark-petal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#D89B9F" />
        <stop offset="100%" stopColor="#B16569" />
      </linearGradient>
    </defs>
    {withHalo && (
      <circle cx="32" cy="32" r="30" fill="url(#rh-mark-halo)" stroke="#E8C4B6" strokeWidth="0.5" />
    )}
    <g transform="translate(32 32)" fill="url(#rh-mark-petal)">
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <ellipse
          key={deg}
          cx="0"
          cy="-11"
          rx="4.5"
          ry="11"
          opacity="0.5"
          transform={`rotate(${deg})`}
        />
      ))}
    </g>
    <g transform="translate(32 32)">
      <circle r="5.5" fill="#A04F54" />
      <circle r="2.2" fill="#FFEDE5" />
    </g>
  </svg>
);

export default function BrandLogo({
  size = 36,
  variant = 'lockup',
  to = '/',
  showTagline = false,
  className = '',
  asLink = true,
}) {
  const wordmark = (
    <span className="font-display text-ink leading-[1] tracking-[0.01em]" style={{ fontSize: size * 0.55 }}>
      Radiance Hub
    </span>
  );
  const tagline = showTagline ? (
    <span
      className="block uppercase tracking-[0.32em] text-rose-gold"
      style={{ fontSize: Math.max(8, size * 0.18) }}
    >
      Beauty · DIY · Curated
    </span>
  ) : null;

  const content =
    variant === 'mark' ? (
      <BrandMark size={size} />
    ) : variant === 'stacked' ? (
      <div className={`flex flex-col items-center gap-2 ${className}`} data-testid="brand-stacked">
        <BrandMark size={size} />
        {wordmark}
        {tagline}
      </div>
    ) : (
      <div className={`flex items-center gap-2 sm:gap-3 ${className}`} data-testid="brand-lockup">
        <BrandMark size={size} />
        <div className="flex flex-col">
          {wordmark}
          {tagline}
        </div>
      </div>
    );

  if (!asLink) return content;
  return (
    <Link to={to} className="inline-flex items-center group focus:outline-none" data-testid="brand-logo">
      {content}
    </Link>
  );
}
